/**
 * Manpower Calculation Utilities
 * 
 * This module provides functions to calculate manpower requirements,
 * costs, and utilization based on the manpower constants data.
 */

export interface ManpowerConstant {
  work: string;
  unit: string;
  labour: string;
  constant: number;
  labourUnit: string;
  category?: string; // Added for reverse lookups
}

export interface ManpowerRequirement {
  labour: string;
  daysRequired: number;
  unit: string;
}

export interface ManpowerCost {
  labour: string;
  daysRequired: number;
  dailyRate: number;
  totalCost: number;
}

export interface DurationEstimate {
  totalLaborDays: number;
  minDuration: number; // With maximum workers
  estimatedDuration: number; // With typical worker count
  maxDuration: number; // With minimum workers
}

/**
 * Normalize DB constants to internal format
 */
export const normalizeManpowerConstants = (dbConstants: any[]): ManpowerConstant[] => {
  return dbConstants.map(c => ({
    work: c.work_type || c.work,
    unit: c.work_unit || c.unit,
    labour: c.labour_type || c.labour,
    constant: parseFloat(c.constant),
    labourUnit: c.labour_unit || c.labourUnit,
    category: c.category
  }));
};

/**
 * Normalize string for comparison (lowercase, trim, normalize spaces)
 */
const normalizeString = (str: string): string => {
  return (str || '').toLowerCase().trim().replace(/\s+/g, ' ');
};

/**
 * Get manpower constants for a specific work type
 * Uses fuzzy matching to handle case differences and minor spelling variations
 * 
 * @param allConstants - Array of all manpower constants
 * @param category - Work category (e.g., "Masonry", "Electrical")
 * @param workType - Specific work (e.g., "100 mm Thk Block Masonary")
 * @returns Array of manpower constants for that work
 */
export const getManpowerConstants = (
  allConstants: ManpowerConstant[],
  category: string,
  workType: string
): ManpowerConstant[] => {
  const normalizedCategory = normalizeString(category);
  const normalizedWorkType = normalizeString(workType);
  
  // First try exact match (case-insensitive)
  let matches = allConstants.filter(item =>
    normalizeString(item.category) === normalizedCategory && 
    normalizeString(item.work) === normalizedWorkType
  );
  
  // If no exact match, try partial/fuzzy matching
  if (matches.length === 0) {
    matches = allConstants.filter(item => {
      const itemCategory = normalizeString(item.category);
      const itemWork = normalizeString(item.work);
      
      // Category must match
      if (itemCategory !== normalizedCategory) return false;
      
      // Work type fuzzy match - check if one contains the other
      return itemWork.includes(normalizedWorkType) || 
             normalizedWorkType.includes(itemWork) ||
             // Also try matching without "work" suffix
             normalizedWorkType.replace(' work', '') === itemWork.replace(' work', '');
    });
  }
  
  return matches;
};

/**
 * Check if two units are compatible for manpower calculation
 * 
 * @param taskUnit - Unit from task (e.g., "LS", "Sqm")
 * @param constantUnit - Unit from manpower constant (e.g., "No", "Sqm")
 * @returns true if units are compatible
 */
const areUnitsCompatible = (taskUnit: string, constantUnit: string): boolean => {
  // Exact match
  if (taskUnit === constantUnit) return true;

  // LS (Lump Sum) is compatible with No (Number)
  if ((taskUnit === 'LS' || taskUnit === 'ls') && constantUnit === 'No') return true;

  // Case-insensitive match
  if (taskUnit.toLowerCase() === constantUnit.toLowerCase()) return true;

  return false;
};

/**
 * Calculate manpower requirements for a task
 * 
 * Formula: Labor Days = Quantity × Constant
 * 
 * @param allConstants - Array of all manpower constants
 * @param category - Work category
 * @param workType - Specific work type
 * @param quantity - Quantity of work (e.g., 20 Sqm)
 * @param unit - Unit of measurement (e.g., "Sqm", "LS")
 * @returns Array of manpower requirements
 */
export const calculateManpowerRequirements = (
  allConstants: ManpowerConstant[],
  category: string,
  workType: string,
  quantity: number,
  unit?: string
): ManpowerRequirement[] => {
  const constants = getManpowerConstants(allConstants, category, workType);

  if (constants.length === 0) {
    return [];
  }

  // Filter by unit if specified, using compatibility check
  const filteredConstants = unit
    ? constants.filter(c => areUnitsCompatible(unit, c.unit))
    : constants;

  if (filteredConstants.length === 0 && unit) {
    console.warn(`No constants found for ${category} - ${workType} with compatible unit: ${unit} (constants have: ${constants.map(c => c.unit).join(', ')})`);
    return [];
  }

  return filteredConstants.map(constant => ({
    labour: constant.labour,
    daysRequired: quantity * constant.constant,
    unit: constant.labourUnit
  }));
};

/**
 * Calculate manpower costs
 * 
 * @param allConstants - Array of all manpower constants
 * @param category - Work category
 * @param workType - Specific work type
 * @param quantity - Quantity of work
 * @param laborRates - Daily rates for each labor type (e.g., { "Mason": 800, "Helper": 500 })
 * @param unit - Unit of measurement (optional)
 * @returns Array of manpower costs
 */
export const calculateManpowerCosts = (
  allConstants: ManpowerConstant[],
  category: string,
  workType: string,
  quantity: number,
  laborRates: Record<string, number>,
  unit?: string
): ManpowerCost[] => {
  const requirements = calculateManpowerRequirements(allConstants, category, workType, quantity, unit);

  return requirements.map(req => {
    const dailyRate = laborRates[req.labour] || 0;
    return {
      labour: req.labour,
      daysRequired: req.daysRequired,
      dailyRate,
      totalCost: req.daysRequired * dailyRate
    };
  });
};

/**
 * Estimate task duration based on available workforce
 * 
 * @param allConstants - Array of all manpower constants
 * @param category - Work category
 * @param workType - Specific work type
 * @param quantity - Quantity of work
 * @param workersAvailable - Number of workers available for each labor type
 * @param unit - Unit of measurement (optional)
 * @returns Duration estimate in days
 */
export const estimateTaskDuration = (
  allConstants: ManpowerConstant[],
  category: string,
  workType: string,
  quantity: number,
  workersAvailable: Record<string, number>,
  unit?: string
): DurationEstimate => {
  const requirements = calculateManpowerRequirements(allConstants, category, workType, quantity, unit);

  if (requirements.length === 0) {
    return {
      totalLaborDays: 0,
      minDuration: 0,
      estimatedDuration: 0,
      maxDuration: 0
    };
  }

  // Calculate duration for each labor type
  const durations = requirements.map(req => {
    const workers = workersAvailable[req.labour] || 1;
    return req.daysRequired / workers;
  });

  // Task duration is limited by the slowest labor type
  const maxDuration = Math.max(...durations);
  const totalLaborDays = requirements.reduce((sum, req) => sum + req.daysRequired, 0);

  return {
    totalLaborDays,
    minDuration: Math.ceil(maxDuration * 0.8), // Optimistic (20% buffer)
    estimatedDuration: Math.ceil(maxDuration),
    maxDuration: Math.ceil(maxDuration * 1.2) // With 20% contingency
  };
};

/**
 * Get total labor cost for a task
 * 
 * @param costs - Array of manpower costs
 * @returns Total cost
 */
export const getTotalLaborCost = (costs: ManpowerCost[]): number => {
  return costs.reduce((sum, cost) => sum + cost.totalCost, 0);
};

/**
 * Calculate labor utilization percentage
 * 
 * @param actualDays - Actual days worked
 * @param estimatedDays - Estimated days required
 * @returns Utilization percentage (>100% means overrun, <100% means efficient)
 */
export const calculateLaborUtilization = (
  actualDays: number,
  estimatedDays: number
): number => {
  if (estimatedDays === 0) return 0;
  return (actualDays / estimatedDays) * 100;
};

/**
 * Get all work types for a category
 * 
 * @param allConstants - Array of all manpower constants
 * @param category - Work category
 * @returns Array of unique work types
 */
export const getWorkTypesForCategory = (
  allConstants: ManpowerConstant[],
  category: string
): string[] => {
  const normalizedCategory = normalizeString(category);
  const categoryItems = allConstants.filter(c => 
    normalizeString(c.category) === normalizedCategory
  );

  if (categoryItems.length === 0) {
    return [];
  }

  // Get unique work types
  const workTypes = new Set(categoryItems.map(item => item.work));
  return Array.from(workTypes);
};

/**
 * Get all labor types required for a category
 * 
 * @param allConstants - Array of all manpower constants
 * @param category - Work category
 * @returns Array of unique labor types
 */
export const getLaborTypesForCategory = (
  allConstants: ManpowerConstant[],
  category: string
): string[] => {
  const categoryItems = allConstants.filter(c => c.category === category);

  if (categoryItems.length === 0) {
    return [];
  }

  const laborTypes = new Set(categoryItems.map(item => item.labour));
  return Array.from(laborTypes);
};

/**
 * Find similar work types (fuzzy search)
 * 
 * @param allConstants - Array of all manpower constants
 * @param category - Work category
 * @param searchTerm - Search term
 * @returns Array of matching work types
 */
export const findSimilarWorkTypes = (
  allConstants: ManpowerConstant[],
  category: string,
  searchTerm: string
): string[] => {
  const workTypes = getWorkTypesForCategory(allConstants, category);
  const lowerSearch = searchTerm.toLowerCase();

  return workTypes.filter(work =>
    work.toLowerCase().includes(lowerSearch)
  );
};

